/**
 * PSP Administrators Card
 *
 * Displays and manages PSP admin users.
 * Same UI pattern as InstitutionAdminsCard but for PSP organizations.
 * Uses the same GraphQL invitation flow — the server determines the role
 * based on organization type.
 */

import { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Plus, Users, Trash2, Mail, CheckCircle, Clock, AlertTriangle, Loader2, XCircle,
} from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { cn } from '@/lib/utils';
import {
  GET_INSTITUTION_ADMINS,
  INVITE_INSTITUTION_ADMIN,
  RESEND_INVITATION,
  REVOKE_INVITATION,
} from '@/graphql/invitations';

interface PspAdmin {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  name: string;
  phone: string | null;
  status: string;
  role: string | null;
  lastUpdatedAt: string | null;
  invitedAt: string | null;
}

interface Props {
  clientId: string;
}

export function PspAdminsCard({ clientId }: Props) {
  // GraphQL query for admins (same endpoint — server determines role by org type)
  const { data, loading, refetch } = useQuery(GET_INSTITUTION_ADMINS, {
    variables: { organizationId: clientId },
  });

  // GraphQL mutations
  const [inviteAdmin, { loading: inviting }] = useMutation(INVITE_INSTITUTION_ADMIN);
  const [resendInvitation, { loading: resending }] = useMutation(RESEND_INVITATION);
  const [revokeInvitation, { loading: revoking }] = useMutation(REVOKE_INVITATION);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState<string | null>(null);
  const [adminError, setAdminError] = useState<string | null>(null);

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const administrators: PspAdmin[] = data?.institutionAdmins || [];

  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setAdminError(null);
  };

  const openAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleInviteAdmin = async () => {
    setAdminError(null);
    try {
      await inviteAdmin({
        variables: {
          organizationId: clientId,
          input: { firstName, lastName, email, phone: phone || undefined },
        },
      });
      setShowAddModal(false);
      resetForm();
      refetch();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to invite administrator';
      setAdminError(message);
    }
  };

  const handleResend = async (adminId: string) => {
    try {
      await resendInvitation({ variables: { invitationId: adminId } });
      refetch();
    } catch (err) {
      console.error('Failed to resend invitation:', err);
    }
  };

  const handleRevoke = async (adminId: string) => {
    try {
      await revokeInvitation({ variables: { invitationId: adminId } });
      setShowRevokeConfirm(null);
      refetch();
    } catch (err) {
      console.error('Failed to revoke invitation:', err);
    }
  };

  const getStatusBadge = (status: string) => {
    const s = status.toUpperCase();
    switch (s) {
      case 'ACTIVE':
        return (
          <span className="badge badge-secondary">
            <CheckCircle className="w-3 h-3" /> Active
          </span>
        );
      case 'INVITED':
        return (
          <span className="badge badge-warning">
            <Clock className="w-3 h-3" /> Invited
          </span>
        );
      case 'PENDING':
        return (
          <span className="badge badge-neutral">
            <Clock className="w-3 h-3" /> Pending
          </span>
        );
      case 'DISABLED':
        return (
          <span className="badge bg-rust/10 text-rust dark:bg-salmon/20 dark:text-salmon">
            <XCircle className="w-3 h-3" /> Disabled
          </span>
        );
      default:
        return <span className="badge badge-neutral">{status}</span>;
    }
  };

  const isActive = (status: string) => status.toUpperCase() === 'ACTIVE';
  const isInvited = (status: string) => status.toUpperCase() === 'INVITED';

  return (
    <>
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-orange dark:text-orange-light" />
                <h2 className="text-lg font-semibold text-slate dark:text-mist">PSP Administrators</h2>
              </div>
              <p className="text-sm text-chalkboard dark:text-rain-cloud mt-1">
                Manage users with admin permissions for this PSP in BLAST
              </p>
            </div>
            <button onClick={openAddModal} className="btn-primary">
              <Plus className="w-4 h-4" />
              Add Administrator
            </button>
          </div>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-chalkboard dark:text-rain-cloud" />
              <p className="text-sm text-chalkboard dark:text-rain-cloud mt-2">Loading administrators...</p>
            </div>
          ) : (
            <>
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Status</th>
                    <th className="w-32">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {administrators.map((admin) => (
                    <tr key={admin.id} className="group">
                      <td className="font-medium text-slate dark:text-mist">{admin.name}</td>
                      <td>
                        <a
                          href={`mailto:${admin.email}`}
                          className="text-pond dark:text-pond-light hover:underline flex items-center gap-1"
                        >
                          <Mail className="w-3 h-3" />
                          {admin.email}
                        </a>
                      </td>
                      <td className="text-chalkboard dark:text-rain-cloud">{admin.phone || '—'}</td>
                      <td>{getStatusBadge(admin.status)}</td>
                      <td>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {isInvited(admin.status) && (
                            <>
                              <button
                                onClick={() => handleResend(admin.id)}
                                className="btn-ghost btn-icon btn-sm text-pond dark:text-pond-light"
                                title="Resend invitation"
                                disabled={resending}
                              >
                                <Mail className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setShowRevokeConfirm(admin.id)}
                                className="btn-ghost btn-icon btn-sm text-rust dark:text-salmon"
                                title="Revoke invitation"
                                disabled={revoking}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          {isActive(admin.status) && (
                            <span className="text-xs text-chalkboard dark:text-rain-cloud px-2">
                              Managed in BLAST
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {administrators.length === 0 && (
                <div className="p-8 text-center text-chalkboard dark:text-rain-cloud">
                  No PSP administrators added yet. Click &quot;Add Administrator&quot; to get started.
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Add Administrator Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => { setShowAddModal(false); resetForm(); }}
        title="Add PSP Administrator"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                First Name
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name"
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                Last Name
              </label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last name"
                className="input"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setAdminError(null); }}
              placeholder="email@psp.com"
              className={cn('input', adminError && 'border-rust dark:border-salmon')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
              Phone Number (Optional)
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(555) 123-4567"
              className="input"
            />
          </div>
          {adminError && (
            <div className="p-3 bg-rust/10 border border-rust/20 rounded-lg flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-rust dark:text-salmon flex-shrink-0 mt-0.5" />
              <p className="text-sm text-rust dark:text-salmon">{adminError}</p>
            </div>
          )}
          <div className="p-3 bg-pond/10 dark:bg-pond-light/10 rounded-lg border border-pond/20 dark:border-pond-light/20">
            <p className="text-sm text-slate dark:text-mist">
              <Mail className="w-4 h-4 inline mr-1" />
              An invitation email will be sent via BLAST to set up their account.
            </p>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={() => { setShowAddModal(false); resetForm(); }}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleInviteAdmin}
              className="btn-primary"
              disabled={!firstName || !lastName || !email || inviting}
            >
              {inviting ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</>
              ) : (
                'Send Invitation'
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Revoke Invitation Confirmation Modal */}
      <Modal
        isOpen={!!showRevokeConfirm}
        onClose={() => setShowRevokeConfirm(null)}
        title="Revoke Invitation"
      >
        <div className="space-y-4">
          <p className="text-slate dark:text-mist">
            Are you sure you want to revoke this invitation? The invitation link will no longer work.
          </p>
          <div className="flex justify-end gap-3">
            <button onClick={() => setShowRevokeConfirm(null)} className="btn-secondary">
              Cancel
            </button>
            <button
              onClick={() => showRevokeConfirm && handleRevoke(showRevokeConfirm)}
              className="btn-primary bg-rust hover:bg-rust/90"
              disabled={revoking}
            >
              {revoking ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Revoking...</>
              ) : (
                <><Trash2 className="w-4 h-4" /> Revoke</>
              )}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
